import React, { useState, useEffect } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useAuth } from '../../Providers/AuthProvider';
import { Confirm } from './../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';

export default function TeamsManager({ getOutOfTheDynamic }) {
  const { authenticatedFetch } = useAuth();
  const { showMessage } = useFloatMessage();

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  // Form states
  const [teamName, setTeamName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Confirm modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:50586/api/';

  const openConfirmationModal = (messageToDisplay, actionFunctionToExecute) => {
    setConfirmModalData({
      message: messageToDisplay,
      actionToYesButton: actionFunctionToExecute,
      textYesButton: 'Sí, confirmar',
      textNoButton: 'Cancelar'
    });
    setShowConfirmModal(true);
  };

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await authenticatedFetch(`${API_URL}Teams`);
      if (res.ok) {
        const result = await res.json();
        if (result.success) {
          setTeams(result.data);
        } else {
          showMessage(result.message || 'Error al cargar equipos', MESSAGE_TYPE.BAD);
        }
      }
    } catch (err) {
      showMessage('No se pudo conectar con el servidor.', MESSAGE_TYPE.BAD);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showMessage('El archivo excede el tamaño máximo permitido de 2MB.', MESSAGE_TYPE.WARNING);
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'teams');

    try {
      const res = await fetch(`${API_URL}Upload/image`, {
        method: 'POST',
        body: formData
      });
      const result = await res.json();
      if (result.success) {
        setLogoUrl(result.url);
        showMessage('Logo subido con éxito.', MESSAGE_TYPE.SUCCESS);
      } else {
        showMessage(result.message || 'Error al subir imagen.', MESSAGE_TYPE.BAD);
      }
    } catch (err) {
      showMessage('Error de red al subir la imagen.', MESSAGE_TYPE.BAD);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) {
      showMessage('El nombre del equipo es obligatorio.', MESSAGE_TYPE.WARNING);
      return;
    }

    const payload = {
      name: teamName,
      logoUrl: logoUrl,
      isActive: isActive
    };

    try {
      const url = editingTeam ? `${API_URL}Teams/${editingTeam.id}` : `${API_URL}Teams`;
      const method = editingTeam ? 'PUT' : 'POST';

      const res = await authenticatedFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await res.json();
      if (result.success) {
        showMessage(editingTeam ? 'Equipo actualizado con éxito.' : 'Equipo creado con éxito.', MESSAGE_TYPE.SUCCESS);
        resetForm();
        fetchTeams();
      } else {
        showMessage(result.message || 'Error al guardar el equipo.', MESSAGE_TYPE.BAD);
      }
    } catch (err) {
      showMessage('Error de red al guardar.', MESSAGE_TYPE.BAD);
    }
  };

  const startEdit = (team) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setLogoUrl(team.logoUrl || '');
    setIsActive(team.isActive);
  };

  const handleDelete = (team) => {
    openConfirmationModal(
      `¿Estás seguro de que deseas desactivar el equipo "${team.name}"?`,
      async () => {
        try {
          const res = await authenticatedFetch(`${API_URL}Teams/${team.id}`, {
            method: 'DELETE'
          });
          const result = await res.json();
          if (result.success) {
            showMessage('Equipo desactivado con éxito.', MESSAGE_TYPE.SUCCESS);
            fetchTeams();
          } else {
            showMessage(result.message || 'Error al desactivar el equipo.', MESSAGE_TYPE.BAD);
          }
        } catch (err) {
          showMessage('Error al conectar con la base de datos.', MESSAGE_TYPE.BAD);
        }
      }
    );
  };

  const resetForm = () => {
    setEditingTeam(null);
    setTeamName('');
    setLogoUrl('');
    setIsActive(true);
  };

  return (
    <SecondaryBase actionToExecute={getOutOfTheDynamic}>
      <div className="container-fluid pb-5">
        <div className="text-center mb-4 text-white">
          <h3 className="fw-bold"><i className="bi bi-shield-shaded me-2 text-info"></i>Catálogo Maestro de Equipos</h3>
          <p className="text-muted small">Crea y modifica los equipos deportivos que compiten en las dinámicas.</p>
        </div>

        <div className="row g-4">
          {/* Formulario */}
          <div className="col-lg-4">
            <div className="card shadow-sm border-0 rounded-4 text-white" style={{ background: '#111827' }}>
              <div className="card-header bg-dark text-white fw-bold d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <span>{editingTeam ? 'Editar Equipo' : 'Nuevo Equipo'}</span>
                {editingTeam && (
                  <button className="btn btn-xs btn-outline-warning" onClick={resetForm}>
                    Nuevo
                  </button>
                )}
              </div>
              <div className="card-body">
                <form onSubmit={handleSave}>
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Nombre del Equipo</label>
                    <input
                      type="text"
                      className="form-control bg-dark border-secondary text-white"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder="Ej. Real Madrid, Xolos"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-bold">Logo del Equipo</label>
                    <input
                      type="file"
                      className="form-control bg-dark border-secondary text-white mb-2"
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                    {uploadingLogo && <div className="spinner-border spinner-border-sm text-info mb-2" role="status" />}
                    
                    {logoUrl && (
                      <div className="d-flex align-items-center gap-2 bg-black bg-opacity-50 p-2 rounded border border-secondary mt-2">
                        <img 
                          src={logoUrl.startsWith('http') ? logoUrl : `${API_URL.replace('/api/', '')}${logoUrl}`} 
                          alt="Logo Preview" 
                          style={{ width: '45px', height: '45px', objectFit: 'contain' }}
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <span className="small text-muted text-truncate flex-fill">{logoUrl}</span>
                        <button type="button" className="btn btn-sm btn-danger py-0 px-1" onClick={() => setLogoUrl('')}>x</button>
                      </div>
                    )}
                  </div>

                  <div className="mb-3 form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="teamActiveSwitch"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <label className="form-check-label small fw-bold text-white" htmlFor="teamActiveSwitch">
                      Equipo Activo
                    </label>
                  </div>

                  <button type="submit" className="btn btn-info w-100 fw-bold text-dark mt-2 shadow-sm">
                    <i className="bi bi-cloud-arrow-up-fill me-1"></i> Guardar Equipo
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Listado */}
          <div className="col-lg-8">
            <div className="card shadow-sm border-0 rounded-4 text-white" style={{ background: '#111827' }}>
              <div className="card-header bg-dark fw-bold text-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <span>Equipos Registrados ({teams.length})</span>
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-info" role="status" />
                  </div>
                ) : teams.length === 0 ? (
                  <div className="text-center p-5 text-muted">No hay equipos registrados actualmente.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-dark table-hover align-middle mb-0" style={{ background: 'transparent' }}>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Logo</th>
                          <th>Nombre</th>
                          <th>Estatus</th>
                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teams.map((t) => (
                          <tr key={t.id}>
                            <td>{t.id}</td>
                            <td>
                              {t.logoUrl ? (
                                <img
                                  src={t.logoUrl.startsWith('http') ? t.logoUrl : `${API_URL.replace('/api/', '')}${t.logoUrl}`}
                                  alt="Logo"
                                  style={{ width: '35px', height: '35px', objectFit: 'contain' }}
                                  onError={(e) => { e.target.src = '/img/overlays/score/sponsor.png'; }}
                                />
                              ) : (
                                <i className="bi bi-shield-fill text-muted fs-4"></i>
                              )}
                            </td>
                            <td>
                              <span className="fw-bold">{t.name}</span>
                            </td>
                            <td>
                              <span className={`badge ${t.isActive ? 'bg-success' : 'bg-danger'}`}>
                                {t.isActive ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="text-end">
                              <div className="d-flex gap-1 justify-content-end">
                                <button className="btn btn-sm btn-outline-info" onClick={() => startEdit(t)} title="Editar">
                                  <i className="bi bi-pencil-fill"></i>
                                </button>
                                {t.isActive && (
                                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(t)} title="Desactivar">
                                    <i className="bi bi-trash-fill"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <Confirm
          show={showConfirmModal}
          data={confirmModalData}
          onHide={() => setShowConfirmModal(false)}
        />
      )}
    </SecondaryBase>
  );
}
